import { createSelector } from 'reselect';
import { startOfDay } from 'date-fns';
import { IEvent } from '../../Models/DTO/Event.model';
import { NeoState } from '../Reducers.interface';
import { addToDate, afterDate, getEndOfWeek } from '../../Hooks/useDateFns';
import { SortByDateAscending } from '../../Helpers/Sorting.helpers';
import { selectTrainerList } from '../provider';
import IUser from '~/Models/DTO/User.model';
import { UserRole } from '~/Models/DTO/Role.model';
import { keyBy } from 'lodash-es';

const selectGroupEventsByDate = (acc: Record<string, IEvent[]>, currentEvent: IEvent) => {
  const dayStart = startOfDay(new Date(currentEvent.start)).toISOString();
  const currentKeys = Object.keys(acc);
  if (currentKeys.includes(dayStart) && acc[dayStart].find((item) => item.id !== currentEvent.id)) {
    return {
      ...acc,
      [dayStart]: [...acc[dayStart], currentEvent],
    };
  }

  return {
    ...acc,
    [dayStart]: [currentEvent],
  };
};

export const selectEventList = (user?: IUser, userRole?: UserRole) => (
  state: NeoState,
): IEvent[] => {
  const { list } = state.events;
  const trainers = selectTrainerList(state);
  const isATrainer = user?.utype !== 'Admin' && userRole === UserRole.TRAINER;
  const arrList = Object.values(list);
  if (isATrainer) {
    const trainersUUIDs = trainers
      .filter((trainer) => trainer.userId === user?.uuid)
      ?.map((trainer) => trainer.trainerId);
    return arrList
      .filter(
        (event) =>
          !!event.trainerIds.filter((trainerId) => !!trainersUUIDs.includes(trainerId)).length,
      )
      .sort((a, b) => SortByDateAscending(a.start, b.start));
  }
  return arrList.sort((a, b) => SortByDateAscending(a.start, b.start));
};

export const selectEventById = (id: string) => (state: NeoState) => state.events.list[id];

const selectGroupedEvents = (user: IUser | undefined, role?: UserRole) =>
  createSelector(selectEventList(user, role), (events) =>
    events.reduce(selectGroupEventsByDate, {}),
  );

export const selectGroupedEventsByDate = (date: Date, user?: IUser, role?: UserRole) =>
  createSelector(selectGroupedEvents(user, role), (events) => {
    const weekEvents = [events[startOfDay(date).toISOString()] ?? []];

    for (let i = 1; i < 7; i++) {
      const day = addToDate(date.toString(), { days: i });
      weekEvents.push(events[startOfDay(day).toISOString()] ?? []);
    }

    if (Object.keys(events).length === 0) {
      return [];
    }

    return weekEvents;
  });

const selectCountDays = (groupedEvents: Record<string, IEvent[]>) =>
  Object.keys(groupedEvents).reduce(
    (acc, currentDate) => ({
      ...acc,
      [currentDate]: groupedEvents[currentDate].length,
    }),
    {},
  );

export const selectEventsCount = (user?: IUser, role?: UserRole) =>
  createSelector(selectGroupedEvents(user, role), selectCountDays);

export const selectNextEventDateFromDate = (
  date: string,
  user: IUser | undefined,
  role: UserRole | undefined,
) => (state: NeoState) => {
  const nextPossibleEvents = Object.values(selectEventList(user, role)(state)).filter((event) =>
    afterDate(event.start, date),
  );

  if (nextPossibleEvents.length > 0) {
    return nextPossibleEvents[0].start;
  }
  return addToDate(getEndOfWeek(date).toISOString(), { days: 1 }).toISOString();
};

//EVENT TYPES
export const selectEventCategoryTypeList = (state: NeoState) =>
  state?.events?.eventCategoryType?.list || [];
export const selectEventCategoryTypeListLoading = (state: NeoState) =>
  state?.events?.eventCategoryType?.loading;
export const selectEventCategoryTypeListError = (state: NeoState) =>
  state?.events?.eventCategoryType?.error || null;

//SPORT TYPES
export const selectSportCategoryTypeList = (state: NeoState) =>
  state?.events?.sportCategoryType?.list || [];
export const selectSportCategoryTypeListLoading = (state: NeoState) =>
  state?.events?.sportCategoryType?.loading;
export const selectSportCategoryTypeListError = (state: NeoState) =>
  state?.events?.sportCategoryType?.error || null;

export const selectSportCategoriesMap = createSelector(
  selectSportCategoryTypeList,
  (sportCategories) => keyBy(sportCategories, 'id'),
);
